import React from 'react';
import { SvgIcon, SvgIconProps } from '@mui/material';

/**
 * How a place is drawn in the house map: its icon and colour (the keys the API sends in `StorageLocation.icon` and
 * `color`). The same paths and colours as the Android app (`Home.System.Mobile/Controls/PlaceLook.cs`).
 */
export const PLACE_ICONS: { key: string; name: string }[] = [
  { key: 'fridge', name: 'Frigorífico' }, { key: 'freezer', name: 'Congelador' }, { key: 'pantry', name: 'Despensa' },
  { key: 'cupboard', name: 'Armário' }, { key: 'drawer', name: 'Gavetas' }, { key: 'bathroom', name: 'Casa de banho' },
  { key: 'laundry', name: 'Lavandaria' }, { key: 'garage', name: 'Garagem' }, { key: 'basket', name: 'Cesto' },
  { key: 'box', name: 'Caixa' },
];

export const PLACE_COLORS: { key: string; name: string }[] = [
  { key: 'teal', name: 'Verde-água' }, { key: 'blue', name: 'Azul' }, { key: 'amber', name: 'Âmbar' }, { key: 'coral', name: 'Coral' },
  { key: 'purple', name: 'Roxo' }, { key: 'pink', name: 'Rosa' }, { key: 'green', name: 'Verde' }, { key: 'gray', name: 'Cinzento' },
];

/** 24×24 paths, filled even-odd (holes are subpaths inside others). */
const PATHS: Record<string, string> = {
  fridge: 'M18 2.01L6 2c-1.1 0-2 .89-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.11-.9-1.99-2-1.99zM18 20H6v-9.02h12V20zm0-11H6V4h12v5zM8 5h2v3H8zm0 7h2v5H8z',
  freezer: 'M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22z',
  pantry: 'M3 2h18v20H3z M5 4h14v4.5H5z M5 10.5h14v4.5H5z M5 17h14v3H5z M7 5.5h3v3H7z M12 6.5h2.5v2H12z M7 12h2v3H7z M11 11.5h4v3.5h-4z',
  cupboard: 'M3 3h18v18H3z M5 5h6.5v14H5z M12.5 5H19v14h-6.5z M9 11h1.5v2H9z M13.5 11H15v2h-1.5z',
  drawer: 'M3 3h18v18H3z M5 5h14v4H5z M5 10h14v4H5z M5 15h14v4H5z M10 6.5h4v1h-4z M10 11.5h4v1h-4z M10 16.5h4v1h-4z',
  bathroom: 'M2 11h20v3a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5z M4 13v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1z M5 3h4v2H7v5H5z M6 19.5h2V22H6z M16 19.5h2V22h-2z',
  laundry: 'M4 2h16v20H4z M6 4h12v16H6z M7 5h2v1.5H7z M10.5 5h2v1.5h-2z M12 9a5 5 0 1 0 0 10a5 5 0 1 0 0-10z M12 11a3 3 0 1 0 0 6a3 3 0 1 0 0-6z',
  garage: 'M12 2L2 9v13h20V9z M6 11h12v11H6z M6 13h12v1.5H6z M6 16h12v1.5H6z M6 19h12v1.5H6z',
  basket: 'M3 9h18l-2 12H5z M8 12h1.5v6H8z M11.25 12h1.5v6h-1.5z M14.5 12H16v6h-1.5z M8 9l3-5h2l3 5h-2.3L12 6.2 10.3 9z',
  box: 'M20 2H4c-1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-1-2-2-2zm-5 12H9v-2h6v2zm5-7H4V4h16v3z',
};

/** Per colour: soft background, strong (icon, borders, the shelf board) and text on the soft background. */
const RAMPS: Record<string, { soft: string; strong: string; text: string }> = {
  teal: { soft: '#E1F5EE', strong: '#0F6E56', text: '#085041' },
  blue: { soft: '#E6F1FB', strong: '#185FA5', text: '#0C447C' },
  amber: { soft: '#FAEEDA', strong: '#854F0B', text: '#633806' },
  coral: { soft: '#FAECE7', strong: '#993C1D', text: '#712B13' },
  purple: { soft: '#EEEDFE', strong: '#534AB7', text: '#3C3489' },
  pink: { soft: '#FBEAF0', strong: '#993556', text: '#72243E' },
  green: { soft: '#EAF3DE', strong: '#3B6D11', text: '#27500A' },
  gray: { soft: '#F1EFE8', strong: '#5F5E5A', text: '#444441' },
};

export const placeColors = (color?: string | null) => RAMPS[color ?? ''] ?? RAMPS.gray;

/** The place's icon in its strong colour. */
export function PlaceIcon({ icon, color, ...props }: { icon?: string | null; color?: string | null } & Omit<SvgIconProps, 'color'>) {
  return (
    <SvgIcon {...props} style={{ color: placeColors(color).strong, ...props.style }}>
      <path fillRule="evenodd" d={PATHS[icon ?? ''] ?? PATHS.box} />
    </SvgIcon>
  );
}
